<template>
    <div class="h-full flex flex-col">
        <!-- Toolbar -->
        <div class="p-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
                <h1 class="text-lg font-semibold">Workflow Editor</h1>
                <div class="flex gap-2">
                    <UButton
                        variant="outline"
                        size="sm"
                        @click="createDemoWorkflow"
                    >
                        Load Demo
                    </UButton>
                    <UButton
                        :color="isValid ? 'primary' : 'red'"
                        size="sm"
                        @click="validateWorkflow"
                    >
                        {{ isValid ? 'Valid' : 'Invalid' }}
                    </UButton>
                    <UButton
                        variant="outline"
                        size="sm"
                        @click="autoLayout"
                    >
                        Auto Layout
                    </UButton>
                </div>
            </div>
        </div>

        <!-- Editor -->
        <div class="flex-1 flex">
            <!-- Node Palette -->
            <div class="w-64 p-4 bg-gray-50 border-r">
                <h3 class="font-medium mb-3">Node Types</h3>
                <div class="space-y-2">
                    <NodePalette
                        v-for="nodeType in nodeTypes"
                        :key="nodeType.type"
                        :node-type="nodeType"
                        @create-node="onCreateNode"
                    />
                </div>
            </div>

            <!-- Vue Flow Canvas -->
            <div class="flex-1 relative">
                <!-- Drop zone overlay -->
                <div
                    v-if="isDraggingFromPalette"
                    class="absolute inset-0 z-10 pointer-events-none border-2 border-dashed border-blue-400 bg-blue-50/20 rounded-lg flex items-center justify-center"
                >
                    <div class="text-blue-600 font-medium text-lg">
                        Drop node here to add to workflow
                    </div>
                </div>

                <VueFlow
                    :nodes="nodes"
                    :edges="edges"
                    class="vue-flow-basic"
                    :class="{ 'dragover': isDraggingFromPalette }"
                    :connection-mode="ConnectionMode.Loose"
                    :snap-to-grid="true"
                    :snap-grid="[10, 10]"
                    :default-viewport="{ x: 0, y: 0, zoom: 1 }"
                    :min-zoom="0.2"
                    :max-zoom="2"
                    @nodes-change="onNodesChange"
                    @edges-change="onEdgesChange"
                    @connect="onConnect"
                    @node-drag-stop="onNodeDragStop"
                    @drop="onDrop"
                    @dragover="onDragOver"
                    @dragleave="onDragLeave"
                >
                    <!-- Node Templates -->
                    <template #node-spark="{ data, id }">
                        <SparkNode :id="id" :data="data" />
                    </template>
                    <template #node-action="{ data, id }">
                        <ActionNode :id="id" :data="data" />
                    </template>
                    <template #node-branch="{ data, id }">
                        <BranchNode :id="id" :data="data" />
                    </template>
                    <template #node-join="{ data, id }">
                        <JoinNode :id="id" :data="data" />
                    </template>

                    <Controls />
                    <MiniMap />
                    <Background pattern="dots" />
                </VueFlow>
            </div>

            <!-- Properties Panel -->
            <div class="w-80 p-4 bg-gray-50 border-l">
                <NodePropertiesPanel
                    v-if="selectedNode"
                    :node="selectedNode"
                    @update="onNodeUpdate"
                />
                <div v-else class="text-gray-500 text-center mt-8">
                    Select a node to edit properties
                </div>
            </div>
        </div>

        <!-- Status Bar -->
        <div class="p-2 bg-gray-100 border-t text-sm text-gray-600">
            Nodes: {{ nodes.length }} | Edges: {{ edges.length }} |
            {{ isValid ? '✓ Valid DAG' : '✗ Invalid - ' + validationError }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { VueFlow, ConnectionMode } from '@vue-flow/core'
import { Controls } from '@vue-flow/controls'
import { MiniMap } from '@vue-flow/minimap'
import { Background } from '@vue-flow/background'

// Import our custom nodes
import SparkNode from '@/workflow/components/nodes/SparkNode.vue'
import ActionNode from '@/workflow/components/nodes/ActionNode.vue'
import BranchNode from '@/workflow/components/nodes/BranchNode.vue'
import JoinNode from '@/workflow/components/nodes/JoinNode.vue'
import NodePalette from '@/workflow/components/NodePalette.vue'
import NodePropertiesPanel from '@/workflow/components/NodePropertiesPanel.vue'

// Workflow service
import { useWorkflowEditor } from '@/workflow/composables/useWorkflowEditor'

const {
    nodes,
    edges,
    selectedNode,
    isValid,
    validationError,
    nodeTypes,
    isDraggingFromPalette,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeDragStop,
    onCreateNode,
    onNodeUpdate,
    onDrop,
    onDragOver,
    onDragLeave,
    createDemoWorkflow,
    validateWorkflow,
    autoLayout
} = useWorkflowEditor()
</script>

<style>
@import '@vue-flow/core/dist/style.css';
@import '@vue-flow/core/dist/theme-default.css';
@import '@vue-flow/controls/dist/style.css';
@import '@vue-flow/minimap/dist/style.css';

.vue-flow-basic {
    background: #f8fafc;
}

/* Improve handle styling */
.vue-flow__handle {
    width: 16px !important;
    height: 16px !important;
    border: 2px solid #ffffff !important;
    background: #ffffff !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    opacity: 0.8;
    transition: all 0.2s ease;
    z-index: 10;
    pointer-events: all;
}

.vue-flow__handle:hover {
    opacity: 1;
    transform: scale(1.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
    z-index: 20;
}

.vue-flow__handle.vue-flow__handle-connecting {
    background: #3b82f6 !important;
    opacity: 1;
    transform: scale(1.3);
}

.vue-flow__handle.vue-flow__handle-valid {
    background: #10b981 !important;
}

.vue-flow__handle.vue-flow__handle-top {
    top: -8px !important;
}

.vue-flow__handle.vue-flow__handle-bottom {
    bottom: -8px !important;
}

.vue-flow__handle.vue-flow__handle-left {
    left: -8px !important;
}

.vue-flow__handle.vue-flow__handle-right {
    right: -8px !important;
}

/* Node positioning improvements */
.vue-flow__node {
    cursor: grab;
}

.vue-flow__node.dragging {
    cursor: grabbing;
}

.vue-flow__node.selected {
    box-shadow: 0 0 0 2px #3b82f6 !important;
}

/* Improve connection line styling */
.vue-flow__connection-line {
    stroke: #374151;
    stroke-width: 2;
    stroke-dasharray: 5,5;
}

.vue-flow__edge {
    pointer-events: stroke;
    stroke-width: 2;
}

.vue-flow__edge:hover {
    stroke-width: 3;
}

/* Node selection styling */
.vue-flow__node.selected {
    box-shadow: 0 0 0 2px #3b82f6 !important;
}

/* Drop zone styling */
.vue-flow-basic.dragover {
    background: #f0f9ff !important;
    border: 2px dashed #3b82f6 !important;
}

/* Minimap styling */
.vue-flow__minimap {
    background: #ffffff;
    border: 1px solid #e5e7eb;
}

/* Controls styling */
.vue-flow__controls {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.vue-flow__controls button {
    background: #ffffff;
    border: 1px solid #e5e7eb;
}

.vue-flow__controls button:hover {
    background: #f9fafb;
}
</style>
